function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Sentry from "@sentry/core";
import ColorLib from "color";
import { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { Platform, Pressable, View } from "react-native";
import { Pressable as GHPressable } from "react-native-gesture-handler";
import Animated from "react-native-reanimated";
import { useAccessibilityContext } from "../AccessibilityContext";
import { ActivityIndicator } from "../ActivityIndicator";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { addBreadcrumb } from "../../lib/log";
import { isInteractionPaused } from "../../lib/pauseInteraction";
import { useTheme } from "../../styles";
var AnimatedPressable = Animated.createAnimatedComponent(Pressable);
/**
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-73}
 */ export var Button = /*#__PURE__*/ forwardRef(function Button(props, ref) {
    var _useTheme = useTheme(), theme = _useTheme.theme, Shadow = _useTheme.Shadow;
    var pressedOnceRef = useRef(false);
    var _useState = _sliced_to_array(useState(false), 2), _loading = _useState[0], setLoading = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), pressedIn = _useState1[0], setIsPressedIn = _useState1[1];
    var loading = _loading || props.loading;
    var hasContent = !!(props.text || props.children);
    var _props__accentColor;
    var accentColor = (_props__accentColor = props._accentColor) !== null && _props__accentColor !== void 0 ? _props__accentColor : theme.color.primary100;
    var isAccentColorDark = ColorLib(accentColor).darken(0.1).isDark();
    var _props_size;
    var size = (_props_size = props.size) !== null && _props_size !== void 0 ? _props_size : 'normal';
    var _props_variant;
    var variant = (_props_variant = props.variant) !== null && _props_variant !== void 0 ? _props_variant : 'contained';
    var color = props.unavailable ? variant === 'solid' ? 'white' : theme.color.gray500 : props.color || (variant === 'solid' ? isAccentColorDark ? 'white' : theme.color.primary100 : accentColor);
    var isScreenReaderEnabled = useAccessibilityContext().isScreenReaderEnabled;
    var disabled = props.disabled || props.unavailable || loading;
    var isPressable = !(props._useGestureHandler && Platform.OS !== 'web');
    var Component = isPressable ? props._useAnimated ? AnimatedPressable : Pressable : GHPressable;
    var buttonTheme = theme.button[size];
    var lightened = ColorLib(theme.color.primary100).mix(ColorLib('white'), 0.9).hex();
    var darkened = ColorLib(theme.color.primary100).mix(ColorLib('black'), 0.2).hex();
    var _props_iconSize;
    var iconSize = (_props_iconSize = props.iconSize) !== null && _props_iconSize !== void 0 ? _props_iconSize : buttonTheme.iconSize;
    var style = function(param) {
        var pressed = param.pressed, hovered = param.hovered;
        return [
            {
                backgroundColor: 'white',
                borderRadius: buttonTheme.borderRadius,
                paddingVertical: buttonTheme.paddingVertical,
                paddingHorizontal: hasContent ? buttonTheme.paddingHorizontal : buttonTheme.paddingVertical,
                borderWidth: 2,
                borderColor: accentColor,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: props.alignSelf
            },
            variant === 'solid' ? [
                {
                    backgroundColor: accentColor
                },
                isAccentColorDark ? undefined : Shadow.low
            ] : null,
            variant === 'text' ? {
                borderColor: 'transparent',
                backgroundColor: 'transparent',
                alignSelf: props.alignSelf || 'flex-start',
                paddingHorizontal: 0
            } : null,
            props.disabled ? {
                opacity: 0.5
            } : null,
            props.unavailable ? variant === 'contained' ? {
                borderColor: theme.color.gray500
            } : variant === 'solid' ? {
                backgroundColor: theme.color.gray600,
                borderColor: theme.color.gray600
            } : null : null,
            typeof props.style === 'function' ? props.style({
                pressed: pressed,
                hovered: hovered
            }) : props.style,
            pressed && variant === 'solid' && !props.skipOnPress ? [
                {
                    backgroundColor: isAccentColorDark ? darkened : lightened,
                    borderColor: isAccentColorDark ? darkened : lightened
                },
                Platform.OS === 'web' ? Shadow.low : null
            ] : undefined,
            hovered && variant === 'solid' && !props.skipOnPress ? {
                backgroundColor: isAccentColorDark ? darkened : lightened,
                borderColor: isAccentColorDark ? darkened : lightened
            } : undefined,
            hovered && variant === 'contained' && !props.skipOnPress ? {
                backgroundColor: lightened
            } : undefined,
            pressed && variant === 'contained' && !props.skipOnPress ? [
                {
                    backgroundColor: lightened
                },
                Shadow.low
            ] : undefined
        ];
    };
    // if user doesn't provide a forwarded ref, we use our own
    var defaultRef = useRef(null);
    var finalRef = ref || defaultRef;
    useLayoutEffect(function() {
        // @ts-expect-error
        if ((finalRef === null || finalRef === void 0 ? void 0 : finalRef.current) && props.type === 'submit') {
            // role="button" clobbers the type prop so we must apply it manually
            // @ts-expect-error
            finalRef.current.type = props.type;
        }
    }, [
        props.type,
        finalRef
    ]);
    return /*#__PURE__*/ _jsxs(Component, {
        "aria-label": props['aria-label'],
        accessible: props['aria-label'] ? true : undefined,
        role: "button",
        "aria-disabled": !!(props.disabled || props.unavailable),
        "aria-expanded": props['aria-expanded'],
        accessibilityActions: props.accessibilityActions,
        onAccessibilityAction: props.onAccessibilityAction,
        "aria-hidden": !(props['aria-label'] || props.text),
        ref: finalRef,
        testID: props.testID,
        // disabled prop makes button invisible to iOS screen reader if true
        disabled: !isScreenReaderEnabled && disabled,
        onPressIn: function() {
            return setIsPressedIn(true);
        },
        onPressOut: function() {
            return setIsPressedIn(false);
        },
        onPress: function(evt) {
            if (props.skipOnPress) return;
            if (disabled) return;
            if (isInteractionPaused()) return;
            var _props_testID;
            addBreadcrumb({
                category: 'ui.click',
                message: (_props_testID = props.testID) !== null && _props_testID !== void 0 ? _props_testID : props.text,
                data: {
                    componentType: 'Button',
                    testID: props.testID,
                    icon: props.icon,
                    iconRight: props.iconRight,
                    text: props.text
                }
            });
            if (Platform.OS === 'web' && evt && 'preventDefault' in evt) {
                // Need to handle the following cases on web:
                // 1) Button inside Link (type is undefined) => preventDefault() to prevent double navigation
                // 2a) submit Button w/ onPress inside Form => preventDefault() to prevent double submit
                // 2b) submit Button w/o onPress inside Form => skip preventDefault so form is submitted
                if (props.type !== 'submit' || props.onPress) {
                    evt.preventDefault();
                }
            }
            if (props.pressOnce && pressedOnceRef.current) return;
            pressedOnceRef.current = true;
            if (props.onPress) {
                var result = props.onPress();
                if (result === Promise.resolve(result)) {
                    setLoading(true);
                    // We handle error logic and don't want to swallow the original error
                    void result.catch(function(e) {
                        return e;
                    }).then(function(r) {
                        var _props_icon;
                        var _props_testID, _ref;
                        addBreadcrumb({
                            category: 'ui.click.async-done',
                            message: (_ref = (_props_testID = props.testID) !== null && _props_testID !== void 0 ? _props_testID : props.text) !== null && _ref !== void 0 ? _ref : typeof props.icon === 'string' ? props.icon : (_props_icon = props.icon) === null || _props_icon === void 0 ? void 0 : _props_icon.src,
                            data: {
                                error: _instanceof(r, Error)
                            }
                        });
                        setLoading(false);
                        pressedOnceRef.current = false;
                        if (_instanceof(r, Error)) {
                            Sentry.captureException(r);
                            throw r;
                        }
                    });
                } else {
                    pressedOnceRef.current = false;
                }
            }
        },
        style: isPressable && !props._useAnimated ? style : style({
            pressed: false
        }),
        // @ts-expect-error
        dataSet: {
            button: '',
            'button--text': variant === 'text' ? '' : undefined
        },
        children: [
            loading ? /*#__PURE__*/ _jsx(ActivityIndicator, {
                color: color,
                style: {
                    marginRight: 10
                }
            }) : null,
            props.icon && (!loading || !props.hideIconOnLoading) ? /*#__PURE__*/ _jsx(Icon, {
                name: props.icon,
                color: color,
                size: iconSize || 20,
                style: hasContent ? {
                    marginRight: 10
                } : null
            }) : null,
            props.text ? /*#__PURE__*/ _jsx(Text, {
                text: props.text,
                color: color,
                weight: "semibold",
                textAlign: props.alignSelf === 'center' ? 'center' : undefined,
                style: [
                    props.textStyle,
                    {
                        lineHeight: buttonTheme.lineHeight
                    }
                ],
                size: buttonTheme.fontSize
            }) : props.children,
            props.iconRight && (!props.hideIconOnLoading || !loading) ? /*#__PURE__*/ _jsx(Icon, {
                name: props.iconRight,
                color: color,
                size: iconSize || 20,
                style: hasContent ? {
                    marginLeft: 10
                } : null
            }) : null,
            pressedIn && variant === 'text' && Platform.OS !== 'web' ? /*#__PURE__*/ _jsx(View, {
                style: {
                    height: 1,
                    position: 'absolute',
                    backgroundColor: color,
                    right: 0,
                    left: 0,
                    bottom: 5
                }
            }) : null
        ]
    });
});
